import styled, { keyframes } from 'styled-components';
import {
  MuiBox,
  MuiInput,
  MuiAutoComplete,
  MuiSelect,
  MuiDatePicker,
  MuiTimePicker,
  MuiTextField
} from 'theme/material-ui';
import Avatar from '@material-ui/core/Avatar';
import { Color } from 'modules/styles/colors';
import Typography from 'components/UI/Typography';
import { FontWeight, Spacing, ZIndex } from 'modules/styles/variables';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Icon } from 'components/Icon';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import Clock from '@material-ui/icons/AccessTime';

export const standardWidth = 'width: 300px;';
export const bgColor = `background-color: ${Color.blueHue5};`;
export const standardHeight = `
height: 42px;
min-height: 42px;`;

export const Bold = styled.span`
  font-weight: bold;
`;

export const Term = styled.div`
  padding: 12px 0px;
`;

export const MarginMuiBox = styled(MuiBox)`
  margin: 0 ${Spacing.large}px;
  align-items: center;
`;

export const PaddingMuiBox = styled(MuiBox)`
  padding: ${Spacing.medium}px;
`;

interface StyledAvatarProps {
  active?: boolean;
}

export const StyledAvatar = styled(Avatar)`
  && {
    ${({ active }: StyledAvatarProps) =>
      active &&
      `
  background-color: ${Color.green};
  `}
  }
`;

export const HeadingTypography = styled(Typography)`
  flex-grow: 1;
  margin-left: 32px;
  text-align: center;
  font-weight: ${FontWeight.bold};
`;

interface Props {
  isComplete?: boolean;
}

export const StyledCheckCircleIcon = styled(CheckCircleIcon)<Props>`
  && {
    fill: ${(props: Props) => (props.isComplete ? Color.green : Color.buttonDisabled)};
  }
`;

export const EndIcon = styled(Icon)`
  justify-content: flex-end;
`;

export const CloseBox = styled.div`
  display: block;
  position: absolute;
  top: 0px;
  right: 0px;
  margin: ${Spacing.medium}px;
  z-index: ${ZIndex.low};
  cursor: pointer;
`;

const spin = keyframes`
  from {
    transform: rotate(360deg);
  }

  to {
    transform: rotate(0deg);
  }
`;

export const Spin = styled.div`
  animation: ${spin} 3s linear infinite;
`;

export const TopLeft = styled.div<{ verified: boolean }>`
  display: block;
  position: absolute;
  top: -20px;
  left: -20px;
  margin: ${Spacing.medium}px;
  z-index: ${ZIndex.low};
`;

export const Img = styled.div<{ src?: string; height?: string; backgroundColor?: string }>`
  min-width: ${Spacing.xLarge}px;
  min-height: ${Spacing.xxLarge}px;
  ${props => (props.height ? `height: ${props.height};` : `max-height: ${Spacing.xxxLarge}px;`)}
  width: 100%;

  background-image: url(${props => props.src || ''});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  ${props => (props.backgroundColor ? `background-color: ${props.backgroundColor};` : ``)}
`;

export const AddFileButton = styled.button`
  cursor: pointer;
  border-radius: ${Spacing.medium}px;
  background-color: ${Color.white};
  max-width: 140px;
  padding: ${Spacing.medium}px;
  box-shadow: 0px 3px 3px ${Color.grayLight};
  border-color: ${Color.grayLight3};
`;

export const FloatAccept = styled.div`
  position: absolute;
  display: flex;
  bottom: 5px;
  width: 100%;
`;

export const RotateImage = styled.div`
  position: absolute;
  display: block;
  bottom: 50px;
  right: 5px;
  padding: 2px 5px;
  border-radius: 20px;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transform: scaleX(-1);
`;

export const ImageBox = styled.div<{ margin?: string }>`
  margin: ${props => (props.margin ? props.margin : Spacing.large)}px;
  width: 100%;
  display: block;
`;

export const CenteredTypography = styled(Typography)`
  text-align: center;
  display: 'inline-block';
  text-transform: capitalize;
`;

export const RetakeText = styled(MuiBox)`
  text-align: right;
  color: ${Color.MHlogoLightBlue};
  cursor: pointer;
`;

export const FloatingButton = styled.div`
  position: fixed;
  display: block;
  bottom: 0px;
  margin: ${Spacing.small}px ${Spacing.large}px;
  width: calc(100% - ${Spacing.large * 2}px);
`;

export const StyledRadioGroup = styled(RadioGroup)`
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  border-bottom-width: 0;
  padding: 0;
`;

export const StyledRadio = styled(Radio)`
  && {
    color: ${Color.primary};
  }
`;

export const SkipDiv = styled.div`
  width: fit-content;
  margin: auto;
`;

export const PreRegInputBase = styled(MuiInput)`
  ${bgColor}
  border-radius: 3px;
  ${standardWidth}
  border: thin solid darkgrey;
  &&&:before {
    border-bottom: none;
  }
`;

export const StyledSelect = styled(MuiSelect)`
  ${standardWidth}
  ${standardHeight}
  select {
    padding: 0px 32px 0px 7px;
    ${standardHeight}
    ${bgColor}
  }
`;

export const PreRegInput = styled(PreRegInputBase)<{ as?: string }>`
  ${standardHeight}
  ${props => (props.as !== 'select' ? 'padding-left: 5px;' : null)}
  & input {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const PreRegAutoComplete = styled(MuiAutoComplete)`
  ${standardWidth}
  ${bgColor}
  .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
    padding: 4px;
  }

  .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-input {
    min-height: 36px;
  }
`;

export const StyledDatePicker = styled(MuiDatePicker)<{ hideAsterisk?: boolean }>`
  ${standardWidth}
  ${({ hideAsterisk }) =>
    hideAsterisk
      ? `
  .MuiFormLabel-asterisk {
    display: none;
    }`
      : ''}
`;

export const StyledTimePicker = styled(MuiTimePicker)`
  ${standardWidth}
`;
export const TimeClock = styled(Clock)`
  position: absolute;
  left: 268px;
  bottom: 15px;
  color: rgba(0, 0, 0, 0.54);
`;

export const StyledTextArea = styled(MuiTextField)`
  ${standardWidth}
  ${bgColor}
`;

export const PreRegWidth = styled.div`
  ${standardWidth}
`;
